import { CreditDecision, EFinancingProgram } from '@if/core/types'
import { formatDateTime } from '@if/core/utils'
import { Cancel as CancelIcon } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material'
import { useGetCreditDecisionHistory } from '@src/data/api/credit-application-history/credit-application-history-api'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PageSpinner } from '../../../components'
import { useCurrentLang, usePlanEnumForProgram } from '../../../data/selectors/app-config-selectors'
import { CreditDecisionHistory } from '../../../data/types/IncomesHistory'

interface RowProps {
  decisionRevisions: CreditDecisionHistory
  oldDecisionRevision: CreditDecisionHistory
  financingProgramId: EFinancingProgram
}

const CreditDecisionHistoryHistoryRow = ({ decisionRevisions, oldDecisionRevision, financingProgramId }: RowProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const GetColorAfterComparingValues = (
    newValue: string | undefined | number | null,
    oldValue: string | undefined | number | null,
  ) => {
    if (newValue === oldValue)
      return theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black
    return theme.palette.mode === 'dark' ? theme.palette.error.dark : theme.palette.error.light
  }

  const lang = useCurrentLang()
  const planEnum = usePlanEnumForProgram(financingProgramId)

  return (
    <TableRow>
      <TableCell align="center">{decisionRevisions.applicantFullName}</TableCell>
      <TableCell align="center">{decisionRevisions.coApplicantFullName}</TableCell>
      <TableCell
        align="center"
        style={{
          color: GetColorAfterComparingValues(
            decisionRevisions.normsCreditDecision?.planName,
            oldDecisionRevision.normsCreditDecision?.planName,
          ),
        }}
      >
        {decisionRevisions.normsCreditDecision?.planName != null && planEnum != null
          ? planEnum[decisionRevisions.normsCreditDecision?.planName ?? ''][lang]
          : 'N/A'}
      </TableCell>
      <TableCell
        align="center"
        style={{
          color: GetColorAfterComparingValues(
            decisionRevisions.finalCreditDecision?.interestRate,
            oldDecisionRevision.finalCreditDecision?.interestRate,
          ),
        }}
      >
        {decisionRevisions.finalCreditDecision?.interestRate} %
      </TableCell>
      <TableCell
        align="center"
        style={{
          color: GetColorAfterComparingValues(
            decisionRevisions.finalCreditDecision?.maxAmountFinanced,
            oldDecisionRevision.finalCreditDecision?.maxAmountFinanced,
          ),
        }}
      >
        {decisionRevisions.finalCreditDecision?.maxAmountFinanced}
      </TableCell>
      <TableCell
        align="center"
        style={{
          color: GetColorAfterComparingValues(
            decisionRevisions.finalCreditDecision?.maxPmtAmount,
            oldDecisionRevision.finalCreditDecision?.maxPmtAmount,
          ),
        }}
      >
        {decisionRevisions.finalCreditDecision?.maxPmtAmount}
      </TableCell>
      <TableCell
        align="center"
        style={{
          color: GetColorAfterComparingValues(
            decisionRevisions.finalCreditDecision?.maxTermDuration,
            oldDecisionRevision.finalCreditDecision?.maxTermDuration,
          ),
        }}
      >
        {decisionRevisions.finalCreditDecision?.maxTermDuration}
      </TableCell>
      <TableCell align="center">{formatDateTime(decisionRevisions.finalCreditDecision?.madeOn)}</TableCell>
      <TableCell
        align="center"
        style={{
          color: GetColorAfterComparingValues(
            decisionRevisions.finalCreditDecision?.decision,
            oldDecisionRevision.finalCreditDecision?.decision,
          ),
        }}
      >
        {decisionRevisions.finalCreditDecision?.decision
          ? t(`EDecision.${decisionRevisions.finalCreditDecision?.decision}`, { ns: 'enum' })
          : ''}
      </TableCell>
      <TableCell align="center">
        {decisionRevisions.finalCreditDecision?.decidorId
          ? decisionRevisions.finalCreditDecision?.decidorFullName
          : t('common.system')}
      </TableCell>
    </TableRow>
  )
}

interface TableProps {
  decisionRevisions: CreditDecisionHistory[]
  financingProgramId: EFinancingProgram
}

const CreditDecisionHistoryTable = ({ decisionRevisions, financingProgramId }: TableProps) => {
  const { t } = useTranslation()
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mt: 3, mb: 3 }}>
      <TableContainer sx={{ mt: 2, maxHeight: 800 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">{t('common.applicant')}</TableCell>
              <TableCell align="center">{t('common.coApplicant')}</TableCell>
              <TableCell align="center">{t('common.program')}</TableCell>
              <TableCell align="center">{t('worksheetHistory.rate')}</TableCell>
              <TableCell align="center">{t('common.loan')}</TableCell>
              <TableCell align="center">{t('credit.maxPayment')}</TableCell>
              <TableCell align="center">{t('credit.maxTerme')}</TableCell>
              <TableCell align="center">{t('worksheetHistory.DateTime')}</TableCell>
              <TableCell align="center">{t('credit.decision')}</TableCell>
              <TableCell align="center">{t('worksheetHistory.user')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {decisionRevisions.length !== 0 &&
              decisionRevisions.map((decisionRevision, index, decisionRevisionsArray) => (
                <CreditDecisionHistoryHistoryRow
                  decisionRevisions={decisionRevision}
                  oldDecisionRevision={
                    decisionRevisionsArray[index + 1 === decisionRevisionsArray.length ? index : index + 1]
                  }
                  key={decisionRevision.finalCreditDecision?.madeOn}
                  financingProgramId={financingProgramId}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

interface Props {
  onClose: () => void
  open: boolean
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}

const CreditDecisionHistoryDialog = ({ onClose, open, creditApplicationId, financingProgramId }: Props) => {
  const { t } = useTranslation()

  const [rawDecisionHistory, isLoading] = useGetCreditDecisionHistory(
    creditApplicationId,
    financingProgramId,
    open,
  )

  const decisionHistory = useMemo(() => {
    const isRevised = (previousDecision: CreditDecision | null, currentDecision: CreditDecision | null) =>
      previousDecision?.decision !== currentDecision?.decision ||
      previousDecision?.interestRate !== currentDecision?.interestRate ||
      previousDecision?.maxAmountFinanced !== currentDecision?.maxAmountFinanced ||
      previousDecision?.maxPmtAmount !== currentDecision?.maxPmtAmount ||
      previousDecision?.maxTermDuration !== currentDecision?.maxTermDuration

    const filteredHistory: CreditDecisionHistory[] = []
    let previousDecision: CreditDecision | null = null

    rawDecisionHistory.forEach((history) => {
      if (isRevised(previousDecision, history.finalCreditDecision)) filteredHistory.push(history)
      previousDecision = history.finalCreditDecision
    })

    return filteredHistory
  }, [rawDecisionHistory])

  return (
    <Dialog
      onClose={onClose}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xl"
      keepMounted
      transitionDuration={0}
      disablePortal
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title">
        {t('history.decision')}
      </DialogTitle>
      <DialogContent>
        <PageSpinner isLoading={isLoading} />
        <CreditDecisionHistoryTable decisionRevisions={decisionHistory} financingProgramId={financingProgramId} />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="warning" startIcon={<CancelIcon />} onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(CreditDecisionHistoryDialog)
